/**
 * Polling with visibility pause and leak-safe teardown.
 */
import {
  getPaper,
  getOpsStatus,
  getBotStatus,
  BETS_PAGE,
} from '../api.js'
import {
  paperData,
  opsStatus,
  bets,
  betsHasMore,
  notePollResult,
  offlineMode,
  applyExecution,
  horizonFilter,
  assetFilter,
  paperDay,
  paperCalendar,
} from '../stores.js'
import { get } from 'svelte/store'
import { monthOfDay, mskToday } from './tradingDay.js'

const REFRESH_MS = 30000

let timer = null
let stopped = true
let inFlight = false
let queuedRefresh = false
let queuedResetBets = false

/**
 * @param {{ resetBets?: boolean }} [opts]
 *   resetBets — replace the bets feed (Trades refresh / horizon switch).
 *   Poll leaves a multi-page feed alone so «Ещё» is not wiped every 30s.
 */
export async function refreshCore({ resetBets = false } = {}) {
  if (inFlight) {
    // User-triggered refresh / horizon switch must not get lost behind an active poll.
    queuedRefresh = true
    queuedResetBets = queuedResetBets || !!resetBets
    return
  }
  if (typeof document !== 'undefined' && document.hidden) return
  inFlight = true
  try {
    const hz = get(horizonFilter) || 'all'
    const asset = get(assetFilter) || 'all'
    const day = get(paperDay) || ''
    const month = day ? monthOfDay(day) : monthOfDay(mskToday())
    const [paperPack, opsPack] = await Promise.all([
      getPaper({ betsOffset: 0, betsLimit: BETS_PAGE, horizon: hz, asset, day, month }),
      getOpsStatus(),
    ])
    const paperOk = !!(paperPack.res && paperPack.res.ok && paperPack.data && paperPack.data.ok)
    const opsOk = !!(opsPack.res && opsPack.res.ok && opsPack.data && opsPack.data.ok)

    if (paperOk) {
      paperData.set(paperPack.data)
      applyExecution(paperPack.data)
      if (paperPack.data.calendar) paperCalendar.set(paperPack.data.calendar)
      if (paperPack.data.day && !get(paperDay)) {
        paperDay.set(String(paperPack.data.day))
      }
      const paginated = get(bets).length > BETS_PAGE
      if (resetBets || !paginated) {
        bets.set(paperPack.data.feed?.bets || [])
        betsHasMore.set(!!(paperPack.data.feed && paperPack.data.feed.has_more))
      }
    }
    if (opsOk) {
      opsStatus.set(getBotStatus(opsPack.data))
      applyExecution(opsPack.data)
    }

    // Offline after 2 consecutive fails on paper OR ops status
    if (paperOk && opsOk) notePollResult(true)
    else if (!paperOk || !opsOk) notePollResult(false)
  } catch {
    notePollResult(false)
  } finally {
    inFlight = false
    if (queuedRefresh) {
      const nextReset = queuedResetBets
      queuedRefresh = false
      queuedResetBets = false
      queueMicrotask(() => {
        refreshCore({ resetBets: nextReset })
      })
    }
  }
}

function schedule() {
  clearTimer()
  if (stopped) return
  timer = setInterval(() => {
    refreshCore()
  }, REFRESH_MS)
}

function clearTimer() {
  if (timer != null) {
    clearInterval(timer)
    timer = null
  }
}

function onVisibility() {
  if (document.hidden) {
    clearTimer()
  } else if (!stopped) {
    refreshCore()
    schedule()
  }
}

export function startPolling() {
  stopped = false
  refreshCore({ resetBets: true })
  schedule()
  document.addEventListener('visibilitychange', onVisibility)
}

export function stopPolling() {
  stopped = true
  clearTimer()
  document.removeEventListener('visibilitychange', onVisibility)
}

export function isOffline() {
  let v = false
  offlineMode.subscribe((x) => (v = x))()
  return v
}
